<template>
  <div>
    <div class="flex items-center justify-between pt-3">
      <div>
        <NewItemDropdown
          :items="newItemOptions"
          @item-selected="launchNewItemModal"
        />
        <d-btn size="sm" class="ml-2" @click="triggerCollapseAll()">
          Collapse All
        </d-btn>
      </div>
      <div>
        <d-btn color="secondary" size="sm" :loading="saving" @click="saveDraggable">
          Save Changes
        </d-btn>
      </div>
    </div>
    <div class="flex divide-x-4 divide-secondary-400 -mx-10" :class="{'py-10' :categorizedFiles.length > 0}">
      <div class="w-1/2 px-10">
        <categorized-files-tree v-model="categorizedFiles" :data="categorizedFiles" group="files" :collapse-all="collapseAll" />
      </div>
      <div class="w-1/2 px-10">
        <p class="text-3xl font-bold mb-5">
          Uncategorized Files
        </p>
        <draggable
          v-if="uncategorizedFiles.length > 0"
          :list="uncategorizedFiles"
          :group="{name: 'files', put: false, pull: true}"
          class="divide-y divide-secondary-500"
          ghost-class="pb-5"
          @start="drag = true"
          @end="drag = false"
          item-key="id"
        >
          <template #item="{element}">
            <list-item :file="element" class="cursor-pointer" />
          </template>
        </draggable>
        <div v-else>
          No uncategorized files are available at this time.
        </div>
      </div>
    </div>

    <!-- Modals -->
    <d-modal
      :active="showFolderModal"
      :width="850"
      @close="closeFolderModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <FolderModal
        v-bind="currentModalProps"
        @close="closeFolderModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showLinkModal"
      :width="850"
      @close="closeLinkModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <LinkModal
        v-bind="currentModalProps"
        @close="closeLinkModal"
        @saved="handleModalSaved"
      />
    </d-modal>
  </div>
</template>

<script>
  import { getItems, bulkUpdateItems } from '@resources/scripts/services/contentListingItemsApi.service';
  import { ContentListingType, ListingItemType } from '@resources/scripts/constants';
  import Draggable from 'vuedraggable';
  import ListItem from './ListItem/ListItem.vue';
  import CategorizedFilesTree from './Organization/CategorizedFilesTree.vue';
  import NewItemDropdown from './Organization/NewItemDropdown.vue';
  import FolderModal from './Organization/FolderModal.vue';
  import LinkModal from './Organization/LinkModal.vue';

  export default {
    inject: ['contentListing'],
    provide() {
      return {
        getFiles: async () => this.getFiles(),
        saveDraggable: async () => this.saveDraggable(),
      };
    },

    components: {
      CategorizedFilesTree,
      Draggable,
      ListItem,
      NewItemDropdown,
      FolderModal,
      LinkModal,
    },

    data() {
      return {
        ListingItemType,
        collapseAll: false,
        saving: false,
        loading: true,
        uncategorizedFiles: [],
        categorizedFiles: [],
        showFolderModal: false,
        showLinkModal: false,
        currentModalProps: {},
        newItemOptions: [
          { label: 'New Folder', value: ListingItemType.FOLDER },
          { label: 'New Link', value: ListingItemType.LINK }
        ]
      };
    },

    mounted() {
      this.getFiles();
    },

    methods: {
      async triggerCollapseAll() {
        this.collapseAll = true;
        await this.$nextTick();
        this.collapseAll = false;
      },

      async getFiles() {
        this.loading = true;
        this.uncategorizedFiles = await getItems(this.contentListing.id, this.contentListing.type, false);
        this.categorizedFiles = await getItems(this.contentListing.id, this.contentListing.type, true);
        this.loading = false;
      },

      async saveDraggable() {
        this.saving = true;
        const itemsArray = JSON.parse(JSON.stringify(this.categorizedFiles));
        let flattenedArray = this.flattenArray(itemsArray);
        flattenedArray = flattenedArray.map((item, index) => {
          item.sortIndex = index;
          return {
            id: item.id,
            item,
          };
        });
        await bulkUpdateItems(flattenedArray, ContentListingType.STATIC);
        this.saving = false;
        return flattenedArray.map((m) => m.item);
      },

      flattenArray(array, parentId = null) {
        let result = [];
        array.forEach((a) => {
          a.parentFolderId = parentId;
          a.categorized = true;
          result.push(a);
          if (a.childItems.length > 0) {
            result = result.concat(this.flattenArray(a.childItems, a.id));
          }
        });
        return result;
      },

      launchNewItemModal(itemType) {
        this.currentModalProps = {
          contentListing: this.contentListing,
        };

        switch (itemType) {
          case ListingItemType.FOLDER:
            this.showFolderModal = true;
            break;
          case ListingItemType.LINK:
            this.showLinkModal = true;
            break;
          default:
            return;
        }
      },

      closeFolderModal() {
        this.showFolderModal = false;
        this.currentModalProps = {};
      },

      closeLinkModal() {
        this.showLinkModal = false;
        this.currentModalProps = {};
      },

      async handleModalSaved() {
        await this.getFiles();
        await this.saveDraggable();
      },
    },
  };
</script>